import { useCallback, useEffect, useRef } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import {
    isNotificationQueueModalVisibleAtom,
    notificationQueueAtom,
    notificationQueueFilterAtom,
    notificationQueuePageAtom,
    notificationQueueSortAtom,
} from '@/atoms/notifications'
import {
    getNotificationQueue,
    toggleNotificationComplete as toggleNotificationCompleteAPI,
    batchToggleNotificationComplete as batchToggleNotificationCompleteAPI,
    updateNotificationNotes as updateNotificationNotesAPI,
} from '@/api/cb/notification'
import { frontendDisplayedCourseSelector } from '@/atoms/auth'
import { useLogger } from '@/hooks/useLogger'

export const useNotificationQueue = () => {
    const logger = useLogger(useNotificationQueue.name)
    const isVisible = useRecoilValue(isNotificationQueueModalVisibleAtom)
    const courseName = useRecoilValue(frontendDisplayedCourseSelector)
    const [queueData, setQueueData] = useRecoilState(notificationQueueAtom)
    const filter = useRecoilValue(notificationQueueFilterAtom)
    const sort = useRecoilValue(notificationQueueSortAtom)
    const [page, setPage] = useRecoilState(notificationQueuePageAtom)
    const loggerRef = useRef(logger)
    loggerRef.current = logger

    const fetchQueue = useCallback(async () => {
        try {
            const response = await getNotificationQueue(
                courseName,
                page,
                20,
                filter,
                sort
            )
            setQueueData(response.data.payload)
        } catch (e) {
            loggerRef.current.error('Failed to fetch notification queue', e)
        }
    }, [courseName, page, filter, sort, setQueueData])

    useEffect(() => {
        if (isVisible) {
            fetchQueue()
        }
    }, [isVisible, courseName, page, filter, sort, fetchQueue])

    // Auto-page-advance: if the current page is empty but there are still
    // items on earlier pages, go back one page.
    useEffect(() => {
        if (
            queueData &&
            queueData.notifications.length === 0 &&
            queueData.totalCount > 0 &&
            page > 1
        ) {
            setPage(page - 1)
        }
    }, [queueData, page, setPage])

    const toggleComplete = useCallback(
        async (notificationId: string, isCompleted: boolean) => {
            // Optimistic update — remove from list if filter excludes the new state
            setQueueData((prev) => {
                if (!prev) return prev
                if (
                    (filter === 'incomplete' && isCompleted) ||
                    (filter === 'complete' && !isCompleted)
                ) {
                    return {
                        ...prev,
                        notifications: prev.notifications.filter(
                            (n) => n.id !== notificationId
                        ),
                        totalCount: prev.totalCount - 1,
                    }
                }
                return {
                    ...prev,
                    notifications: prev.notifications.map((n) =>
                        n.id === notificationId ? { ...n, isCompleted } : n
                    ),
                }
            })
            try {
                await toggleNotificationCompleteAPI(
                    courseName,
                    notificationId,
                    isCompleted
                )
            } catch (e) {
                loggerRef.current.error(
                    'Failed to toggle notification complete',
                    e
                )
                fetchQueue()
            }
        },
        [courseName, filter, setQueueData, fetchQueue]
    )

    const toggleAllOnPage = useCallback(
        async (isCompleted: boolean) => {
            if (!queueData?.notifications?.length) return
            const ids = queueData.notifications.map((n) => n.id)
            const shouldRemove =
                (filter === 'incomplete' && isCompleted) ||
                (filter === 'complete' && !isCompleted)

            // Optimistic update
            setQueueData((prev) => {
                if (!prev) return prev
                if (shouldRemove) {
                    return {
                        ...prev,
                        notifications: [],
                        totalCount: prev.totalCount - ids.length,
                    }
                }
                return {
                    ...prev,
                    notifications: prev.notifications.map((n) => ({
                        ...n,
                        isCompleted,
                    })),
                }
            })

            try {
                await batchToggleNotificationCompleteAPI(
                    courseName,
                    ids,
                    isCompleted
                )
            } catch {
                // Fallback: call individual toggles if batch endpoint unavailable
                await Promise.all(
                    ids.map((id) =>
                        toggleNotificationCompleteAPI(
                            courseName,
                            id,
                            isCompleted
                        ).catch(() => {})
                    )
                )
            }
            // Re-fetch to get next page's items
            if (shouldRemove) {
                await fetchQueue()
            }
        },
        [courseName, filter, queueData, setQueueData, fetchQueue]
    )

    const saveNotes = useCallback(
        async (notificationId: string, notes: string) => {
            try {
                await updateNotificationNotesAPI(
                    courseName,
                    notificationId,
                    notes
                )
            } catch (e) {
                loggerRef.current.error('Failed to save notification notes', e)
            }
        },
        [courseName]
    )

    return { queueData, fetchQueue, toggleComplete, toggleAllOnPage, saveNotes }
}
